import type { ReactNode } from 'react'
import type { ThemeTokens } from '../theme'

export interface ChartCardProps {
  title: string
  subtitle?: string
  tokens: ThemeTokens
  /** Chart body height; every chart in the system shares one of a small set
   *  of heights so the Dashboards grid reads as one consistent system rather
   *  than a scatter of ad-hoc sizes. */
  height?: string
  children: ReactNode
}

/**
 * Titled card wrapper shared by every chart in the admin panel (Task 9's
 * "one consistent chart system"): same surface, border, radius and title
 * treatment as the `section` cards on the Task 8 pages
 * (`styles.ts`'s `pageStyles().section`), so Dashboards reads as part of the
 * same app rather than a bolted-on charting library demo.
 */
export function ChartCard({ title, subtitle, tokens, height = '16rem', children }: ChartCardProps) {
  return (
    <div
      style={{
        background: tokens.surface,
        border: `1px solid ${tokens.border}`,
        borderRadius: '1rem',
        padding: '1.4rem 1.5rem',
        flex: '1 1 22rem',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: '0.9rem',
      }}
    >
      <div>
        <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, color: tokens.textPrimary }}>{title}</h3>
        {subtitle ? (
          <p style={{ margin: '0.25rem 0 0', fontSize: '0.78rem', color: tokens.textSecondary, lineHeight: 1.4 }}>
            {subtitle}
          </p>
        ) : null}
      </div>
      {/* Round 13, Maroun's screenshot: a table holding a long unbroken
          string (an unnormalised model id) could still force this card
          wider than its flex track and scroll the whole PAGE sideways,
          even with the card's own `minWidth: 0` above -- that guards the
          FLEX ITEM, not this block child, which needs its own explicit
          `minWidth: 0`/`maxWidth: 100%` to refuse to grow past its parent
          regardless of what a descendant table wants to be. Every table in
          this file already scrolls its OWN overflow (`overflowX: 'auto'`
          in CostPer1kTable/CoverageTable); this is the card-level half of
          that fix. */}
      <div style={{ width: '100%', minWidth: 0, maxWidth: '100%', height }}>{children}</div>
    </div>
  )
}

export default ChartCard
